import './App.css';
import {ConfigProvider, Spin} from 'antd';
import {jwtDecode} from 'jwt-decode';
import {Suspense, lazy, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Route, BrowserRouter as Router, Routes} from 'react-router-dom';

import {ProtectedRoute} from './component/common/ProtectedRoute';
import {menuData} from './menuData';
import {logoutStart} from './redux/authAction';
import {selectAuth} from './redux/selectors';

// Lazy loaded components
const PageNotFound = lazy(() => import('./component/common/PageNotFound').then(m => ({default: m.PageNotFound})));
const Unauthorized = lazy(() => import('./component/common/PageNotFound').then(m => ({default: m.Unauthorized})));
const MainDashboard = lazy(() => import('./screenComponent/MainDashboard').then(m => ({default: m.MainDashboard})));
const LeaveApplication = lazy(() => import('./screenComponent/MainDashboard/ApplicationScreen/LeaveApplication').then(m => ({default: m.LeaveApplication})));
const HomeScreen = lazy(() => import('./screenComponent/MainDashboard/HomeScreen').then(m => ({default: m.HomeScreen})));
const LoginScreen = lazy(() => import('./screenComponent/MainDashboard/LoginScreen').then(m => ({default: m.LoginScreen})));
const AuthLayout = lazy(() => import('./screenComponent/MainDashboard/LoginScreen/AuthLayout'));
const ForgotPasswordScreen = lazy(() => import('./screenComponent/MainDashboard/LoginScreen/ForgotPasswordScreen').then(m => ({default: m.ForgotPasswordScreen})));
const AnnouncementsScreen = lazy(() => import('./screenComponent/MainDashboard/SuperAdmin/Announcements').then(m => ({default: m.AnnouncementsScreen})));
const CollegeScreen = lazy(() => import('./screenComponent/MainDashboard/SuperAdmin/CollegeScreen').then(m => ({default: m.CollegeScreen})));
const UpdateHierarchy = lazy(() => import('./screenComponent/MainDashboard/SuperAdmin/CollegeScreen/UpdateHierarchy').then(m => ({default: m.UpdateHierarchy})));
const DepartmentScreen = lazy(() => import('./screenComponent/MainDashboard/SuperAdmin/DepartmentScreen').then(m => ({default: m.DepartmentScreen})));
const EmployeeScreen = lazy(() => import('./screenComponent/MainDashboard/SuperAdmin/EmployeeScreen').then(m => ({default: m.EmployeeScreen})));
const HolidayScreen = lazy(() => import('./screenComponent/MainDashboard/SuperAdmin/HolidayScreen').then(m => ({default: m.HolidayScreen})));
const LeaveReports = lazy(() => import('./screenComponent/MainDashboard/SuperAdmin/LeaveReports').then(m => ({default: m.LeaveReports})));
const Leaves_Requests = lazy(() => import('./screenComponent/MainDashboard/SuperAdmin/Leaves_Requests').then(m => ({default: m.Leaves_Requests})));
const AllLeavesRequestTable = lazy(() => import('./screenComponent/MainDashboard/SuperAdmin/Leaves_Requests/AllLeaveRequestTable').then(m => ({default: m.AllLeavesRequestTable})));
const UserLeaveHistory = lazy(() => import('./screenComponent/MainDashboard/SuperAdmin/Leaves_Requests/UserLeaveHistory').then(m => ({default: m.UserLeaveHistory})));
const LeavesScreen = lazy(() => import('./screenComponent/MainDashboard/SuperAdmin/LeavesScreen').then(m => ({default: m.LeavesScreen})));

function App() {
  const auth = useSelector(selectAuth);

  const token = auth?.tokenData;

  const userRole = auth?.user.role;

  const {loggedIn} = useSelector(state => state.auth);

  const dispatch = useDispatch();

  const menuOptions= menuData[userRole] || [];
  
  useEffect(() => {
    if (token) {
      const decodedToken = jwtDecode(token);

      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp < currentTime && loggedIn) {
        dispatch(logoutStart());
      }
    } else if (loggedIn) {
      dispatch(logoutStart());
    }
  }, [token, loggedIn, dispatch]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#a23333',
        },
      }}
    >
      <Router>
        <Suspense fallback={<div className={'flex justify-center'}><Spin size={'small'} /></div>}>
          <Routes>
            <Route path={'/auth'} element={<AuthLayout />}>
              <Route path={'login'} element={<LoginScreen />} />
              <Route path={'forgot-password'} element={<ForgotPasswordScreen />} />
            </Route>

            <Route element={<MainDashboard role1={userRole} menuOptions={menuOptions} />}>
              <Route element={<ProtectedRoute allowedRoles={['ADMIN']} />}>
                <Route path={'/colleges'} element={<CollegeScreen />} />
                <Route path={'/departments'} element={<DepartmentScreen />} />
                <Route path={'/employees'} element={<EmployeeScreen />} />
                <Route path={'/holidays'} element={<HolidayScreen />} />
                <Route path={'/leave_types'} element={<LeavesScreen />} />
                <Route path={'/pending_leave_request'} element= {<AllLeavesRequestTable />} />   
                <Route path={'/update_hierarchy'} element={<UpdateHierarchy />} />       
              </Route>
            
              <Route element={<ProtectedRoute allowedRoles={['PRINCIPAL', 'DIRECTOR', 'ADMIN']} />}>
                <Route path={'/announcements'} element={<AnnouncementsScreen />} />
              </Route>

              <Route element={<ProtectedRoute allowedRoles={['PRINCIPAL', 'DIRECTOR', 'ADMIN', 'ACADEMIC_DEAN', 'ACADEMIC_INCHARGE', 'ACADEMIC_COORDINATOR', 'REGISTRAR', 'OFFICE_SUPERINTENDENT']} />}>
                <Route path={'/leave_report'} element={<LeaveReports />} />
              </Route>
            
              <Route element={<ProtectedRoute allowedRoles={['PRINCIPAL', 'DIRECTOR', 'ADMIN', 'ACADEMIC_DEAN', 'ACADEMIC_INCHARGE', 'ACADEMIC_COORDINATOR', 'HOD', 'REGISTRAR', 'OFFICE_SUPERINTENDENT', 'INCHARGE']} />}>
                <Route path={'/leave_request'} element={<Leaves_Requests />} />   
              </Route>

              <Route path={'/'} element={<HomeScreen />} />
              <Route path={'/apply_leave'} element={<LeaveApplication />} />
              <Route path={'/leave_history'} element = {<UserLeaveHistory />} />   
              <Route path={'/profile'} element = {<EmployeeScreen />} />

              {/*unauthoriszed*/}
              <Route path={'/unauthorized'} element={<Unauthorized />} />
              {/* 404 */}
              <Route path={'*'} element={<PageNotFound />} />   
            </Route>
          </Routes>
        </Suspense>
      </Router>
    </ConfigProvider>
  );
}

export default App;
